import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeHtml, sanitizeUrl } from '../../security/sanitize';
import { useFeedback } from '../../contexts/FeedbackContext';
import { getProfilePlaceholder, getCoverPlaceholder } from '../../utils/placeholders';

import LoadingSpinner from '../../components/LoadingSpinner';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

// Category options for influencers
const CATEGORY_OPTIONS = [
  'Fashion', 'Beauty', 'Lifestyle', 'Travel', 'Food', 'Fitness',
  'Technology', 'Gaming', 'Parenting', 'Business', 'Education', 'Entertainment'
];

const MAX_CATEGORIES = 5;
const MAX_IMAGE_SIZE_MB = 1;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram Profile URL', placeholder: 'https://www.instagram.com/yourusername' },
  { key: 'tiktok', label: 'TikTok Profile URL', placeholder: 'https://www.tiktok.com/@yourusername' },
  { key: 'twitter', label: 'X (Twitter) Profile URL', placeholder: 'https://x.com/yourusername' },
  { key: 'facebook', label: 'Facebook Profile URL', placeholder: 'https://www.facebook.com/yourusername' }
];

const EMPTY_FORM_DATA = {
  displayName: '',
  bio: '',
  location: '',
  phone: '',
  gender: '',
  age: '',
  isAffiliate: '',
  monthlyGMV: '',
  gmvProofUrl: '',
  categories: [],
  socialMedia: { facebook: '', instagram: '', tiktok: '', twitter: '' },
  followers: { facebook: '', instagram: '', tiktok: '', twitter: '' },
  audienceDemographics: {
    ageRanges: { '18-24': '', '25-34': '', '35-44': '', '45-54': '', '55+': '' },
    genderSplit: { male: '', female: '', other: '' },
    topLocations: ['', '', ''],
    interests: []
  }
};

// Reusable text input field — clean label, no icons
const ProfileInput = ({ label, name, value, onChange, disabled, type = 'text', placeholder, required }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out disabled:bg-gray-50 disabled:text-gray-500"
      placeholder={placeholder}
    />
  </div>
);

// Reusable social media field — URL + conditional followers input
const SocialMediaField = ({ platform, formData, onChange, disabled }) => {
  const url = formData.socialMedia[platform.key] || '';
  const followers = formData.followers[platform.key] || '';

  return (
    <div>
      <label htmlFor={platform.key} className="block text-sm font-medium text-gray-700 mb-1">
        {platform.label}
      </label>
      <input
        type="url"
        id={platform.key}
        name={`socialMedia.${platform.key}`}
        value={url}
        onChange={onChange}
        disabled={disabled}
        className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out mb-2 disabled:bg-gray-50 disabled:text-gray-500"
        placeholder={platform.placeholder}
      />
      {url && (
        <input
          type="number"
          id={`${platform.key}-followers`}
          name={`followers.${platform.key}`}
          value={followers}
          onChange={onChange}
          disabled={disabled}
          className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out disabled:bg-gray-50 disabled:text-gray-500"
          placeholder="Number of followers"
          min="0"
        />
      )}
    </div>
  );
};

const Profile = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [originalFormData, setOriginalFormData] = useState(EMPTY_FORM_DATA);
  const [originalImagePreview, setOriginalImagePreview] = useState(null);
  const [originalCoverImagePreview, setOriginalCoverImagePreview] = useState(null);

  const [gmvProofFile, setGmvProofFile] = useState(null);
  const [gmvProofPreview, setGmvProofPreview] = useState(null);
  const [interestInput, setInterestInput] = useState('');

  // Refs to track object URLs for cleanup
  const profileUrlRef = useRef(null);
  const coverUrlRef = useRef(null);
  const gmvProofUrlRef = useRef(null);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      if (profileUrlRef.current) URL.revokeObjectURL(profileUrlRef.current);
      if (coverUrlRef.current) URL.revokeObjectURL(coverUrlRef.current);
      if (gmvProofUrlRef.current) URL.revokeObjectURL(gmvProofUrlRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const loadedData = {
            displayName: userData.displayName || '',
            bio: userData.bio || '',
            location: userData.location || '',
            phone: userData.phone || '',
            gender: userData.gender || '',
            age: userData.age || '',
            isAffiliate: userData.isAffiliate || '',
            monthlyGMV: userData.monthlyGMV || '',
            gmvProofUrl: userData.gmvProofUrl || '',
            categories: Array.isArray(userData.categories) ? userData.categories : [],
            socialMedia: userData.socialMedia || { instagram: '', tiktok: '', twitter: '', facebook: '' },
            followers: userData.followers || { instagram: '', tiktok: '', twitter: '', facebook: '' },
            audienceDemographics: userData.audienceDemographics || {
              ageRanges: { '18-24': '', '25-34': '', '35-44': '', '45-54': '', '55+': '' },
              genderSplit: { male: '', female: '', other: '' },
              topLocations: ['', '', ''],
              interests: []
            }
          };
          setFormData(loadedData);
          setOriginalFormData(loadedData);

          if (userData.profileImageUrl) {
            setImagePreview(userData.profileImageUrl);
            setOriginalImagePreview(userData.profileImageUrl);
          }
          if (userData.coverImageUrl) {
            setCoverImagePreview(userData.coverImageUrl);
            setOriginalCoverImagePreview(userData.coverImageUrl);
          }
        }
      } catch (err) {
        setError('Failed to load profile data. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => {
        const updated = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
        // Clear followers when social media URL is emptied
        if (parent === 'socialMedia' && !value && prev.followers[child]) {
          updated.followers = { ...prev.followers, [child]: '' };
        }
        return updated;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (category) => {
    const currentCategories = Array.isArray(formData.categories) ? formData.categories : [];

    if (currentCategories.includes(category)) {
      setFormData(prev => ({
        ...prev,
        categories: currentCategories.filter(cat => cat !== category)
      }));
    } else {
      if (currentCategories.length >= MAX_CATEGORIES) {
        showError(`You can select up to ${MAX_CATEGORIES} categories.`, 'Remove one before adding another.', 4000);
        return;
      }
      setFormData(prev => ({
        ...prev,
        categories: [...currentCategories, category]
      }));
    }
  };

  const handleAudienceChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      audienceDemographics: {
        ...prev.audienceDemographics,
        [category]: {
          ...prev.audienceDemographics?.[category],
          [field]: value
        }
      }
    }));
  };

  const handleAudienceLocationChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      audienceDemographics: {
        ...prev.audienceDemographics,
        topLocations: prev.audienceDemographics?.topLocations?.map((loc, i) => i === index ? value : loc) || ['', '', '']
      }
    }));
  };

  const handleInterestAdd = () => {
    const trimmed = interestInput.trim();
    if (trimmed && !formData.audienceDemographics?.interests?.includes(trimmed) && (formData.audienceDemographics?.interests?.length || 0) < 10) {
      setFormData(prev => ({
        ...prev,
        audienceDemographics: {
          ...prev.audienceDemographics,
          interests: [...(prev.audienceDemographics?.interests || []), trimmed]
        }
      }));
      setInterestInput('');
    }
  };

  const handleInterestRemove = (interest) => {
    setFormData(prev => ({
      ...prev,
      audienceDemographics: {
        ...prev.audienceDemographics,
        interests: prev.audienceDemographics?.interests?.filter(i => i !== interest) || []
      }
    }));
  };

  const validateFileSize = (file) => {
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      showError(`File is too large.`, `Maximum size is ${MAX_IMAGE_SIZE_MB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`, 5000);
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) return;

    setProfileImage(file);
    if (profileUrlRef.current) URL.revokeObjectURL(profileUrlRef.current);
    const url = URL.createObjectURL(file);
    profileUrlRef.current = url;
    setImagePreview(url);
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) return;

    setCoverImage(file);
    if (coverUrlRef.current) URL.revokeObjectURL(coverUrlRef.current);
    const url = URL.createObjectURL(file);
    coverUrlRef.current = url;
    setCoverImagePreview(url);
  };

  const handleGmvProofChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setGmvProofFile(file);
    if (gmvProofUrlRef.current) URL.revokeObjectURL(gmvProofUrlRef.current);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      gmvProofUrlRef.current = url;
      setGmvProofPreview(url);
    } else {
      gmvProofUrlRef.current = null;
      setGmvProofPreview(null);
    }
  };

  const handleCancelEdit = () => {
    // Restore original data
    setFormData(originalFormData);
    // Clear any pending file selections
    setProfileImage(null);
    setCoverImage(null);
    setGmvProofFile(null);
    if (profileUrlRef.current) {
      URL.revokeObjectURL(profileUrlRef.current);
      profileUrlRef.current = null;
    }
    if (coverUrlRef.current) {
      URL.revokeObjectURL(coverUrlRef.current);
      coverUrlRef.current = null;
    }
    if (gmvProofUrlRef.current) {
      URL.revokeObjectURL(gmvProofUrlRef.current);
      gmvProofUrlRef.current = null;
    }
    // Restore original image previews
    setImagePreview(originalImagePreview);
    setCoverImagePreview(originalCoverImagePreview);
    setGmvProofPreview(null);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      // Validate CSRF token
      const form = new FormData(e.target);
      const csrfToken = form.get('csrfToken');
      if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
        showError('Security validation failed.', 'Please refresh the page and try again.', 5000);
        setSaving(false);
        return;
      }

      // Validate required fields
      if (!formData.displayName.trim()) {
        showError('Display Name is required.', 'Please enter your display name.', 4000);
        setSaving(false);
        return;
      }
      if (!formData.gender) {
        showError('Gender is required.', 'Please select your gender.', 4000);
        setSaving(false);
        return;
      }
      if (!formData.age) {
        showError('Age range is required.', 'Please select your age range.', 4000);
        setSaving(false);
        return;
      }
      if (!formData.isAffiliate) {
        showError('Affiliate status is required.', 'Please indicate if you are an affiliate creator.', 4000);
        setSaving(false);
        return;
      }

      // Validate mandatory photos
      if (!imagePreview && !profileImage) {
        showError('Profile photo is required.', 'Please upload a profile photo.', 5000);
        setSaving(false);
        return;
      }
      if (!coverImagePreview && !coverImage) {
        showError('Cover photo is required.', 'Please upload a cover photo.', 5000);
        setSaving(false);
        return;
      }

      // Sanitize form data (individual sanitization, no double-wrapping)
      const sanitizedData = {
        displayName: sanitizeText(formData.displayName),
        bio: sanitizeHtml(formData.bio),
        location: sanitizeText(formData.location),
        phone: sanitizeText(formData.phone),
        gender: sanitizeText(formData.gender),
        age: sanitizeText(formData.age),
        isAffiliate: sanitizeText(formData.isAffiliate),
        monthlyGMV: formData.monthlyGMV ? parseFloat(formData.monthlyGMV) || 0 : 0,
        categories: formData.categories.map(cat => sanitizeText(cat)),
        socialMedia: {
          facebook: formData.socialMedia.facebook ? sanitizeUrl(formData.socialMedia.facebook) : '',
          instagram: formData.socialMedia.instagram ? sanitizeUrl(formData.socialMedia.instagram) : '',
          tiktok: formData.socialMedia.tiktok ? sanitizeUrl(formData.socialMedia.tiktok) : '',
          twitter: formData.socialMedia.twitter ? sanitizeUrl(formData.socialMedia.twitter) : ''
        },
        followers: {
          facebook: formData.followers.facebook ? parseInt(formData.followers.facebook) || 0 : 0,
          instagram: formData.followers.instagram ? parseInt(formData.followers.instagram) || 0 : 0,
          tiktok: formData.followers.tiktok ? parseInt(formData.followers.tiktok) || 0 : 0,
          twitter: formData.followers.twitter ? parseInt(formData.followers.twitter) || 0 : 0
        },
        audienceDemographics: {
          ageRanges: formData.audienceDemographics?.ageRanges || {},
          genderSplit: formData.audienceDemographics?.genderSplit || {},
          topLocations: formData.audienceDemographics?.topLocations?.filter(l => l) || [],
          interests: formData.audienceDemographics?.interests?.map(i => sanitizeText(i)) || []
        }
      };

      let profileImageUrl = imagePreview;
      let coverImageUrl = coverImagePreview;

      if (profileImage) {
        const profileImageRef = ref(storage, `profiles/${currentUser.uid}/profile-image`);
        await uploadBytes(profileImageRef, profileImage);
        profileImageUrl = await getDownloadURL(profileImageRef);
      }

      if (coverImage) {
        const coverImageRef = ref(storage, `profiles/${currentUser.uid}/cover-image`);
        await uploadBytes(coverImageRef, coverImage);
        coverImageUrl = await getDownloadURL(coverImageRef);
      }

      let gmvProofUrl = formData.gmvProofUrl;
      if (gmvProofFile) {
        const gmvProofRef = ref(storage, `profiles/${currentUser.uid}/gmv-proof_${Date.now()}`);
        await uploadBytes(gmvProofRef, gmvProofFile);
        gmvProofUrl = await getDownloadURL(gmvProofRef);
      }

      const userDocRef = doc(db, 'users', currentUser.uid);
      const updateData = {
        ...sanitizedData,
        profileImageUrl,
        coverImageUrl,
        gmvProofUrl,
        updatedAt: new Date()
      };
      await updateDoc(userDocRef, updateData);

      // Update original snapshot so cancel doesn't revert to pre-save state
      const savedData = { ...formData, gmvProofUrl };
      setOriginalFormData(savedData);
      setFormData(savedData);
      setOriginalImagePreview(profileImageUrl);
      setOriginalCoverImagePreview(coverImageUrl);

      showSuccess('Profile updated successfully!', 'Your profile information has been saved.', 5000);
      setIsEditing(false);
      setProfileImage(null);
      setCoverImage(null);
      setGmvProofFile(null);
      if (profileUrlRef.current) { URL.revokeObjectURL(profileUrlRef.current); profileUrlRef.current = null; }
      if (coverUrlRef.current) { URL.revokeObjectURL(coverUrlRef.current); coverUrlRef.current = null; }
      if (gmvProofUrlRef.current) { URL.revokeObjectURL(gmvProofUrlRef.current); gmvProofUrlRef.current = null; }

    } catch (err) {
      showError('Failed to update profile.', 'Please check your connection and try again.', 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Profile Information</h2>
          <p className="text-gray-600">Manage your influencer profile details</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Profile Information</h2>
        <p className="text-gray-600">Manage your influencer profile details</p>
      </div>

      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Personal Details</h3>
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <CSRFToken />

          {/* Profile Photos */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Profile Photos</h4>

            {/* Cover Photo */}
            <div className="relative mb-6">
              <div className="h-48 w-full bg-gray-200 rounded-lg overflow-hidden">
                <img
                  src={coverImagePreview || getCoverPlaceholder()}
                  alt="Cover"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = getCoverPlaceholder();
                  }}
                />
              </div>
              {isEditing && (
                <div className="absolute bottom-4 right-4">
                  <input
                    type="file"
                    id="coverImage"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="coverImage"
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary-dark cursor-pointer transition-colors"
                  >
                    Change Cover
                  </label>
                </div>
              )}
            </div>

            {/* Profile Photo */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="h-24 w-24 rounded-full overflow-hidden bg-gray-200">
                  <img
                    src={imagePreview || getProfilePlaceholder(formData.displayName || currentUser?.email)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getProfilePlaceholder(formData.displayName || currentUser?.email);
                    }}
                  />
                </div>
                {isEditing && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2">
                    <input
                      type="file"
                      id="profileImage"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="profileImage"
                      className="inline-flex items-center px-2.5 py-1 border border-gray-300 text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      Change
                    </label>
                  </div>
                )}
              </div>
              <div>
                <h5 className="text-sm font-medium text-gray-900">Profile Picture</h5>
                <p className="text-sm text-gray-500">JPG, GIF or PNG. {MAX_IMAGE_SIZE_MB}MB max.</p>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ProfileInput
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="Your display name"
                required
              />

              <ProfileInput
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="City, Country"
              />

              <ProfileInput
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="+63 912 345 6789"
              />

              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                  Gender<span className="text-red-500 ml-0.5">*</span>
                </label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleSelectChange('gender', value)}
                  disabled={!isEditing}
                >
                  <SelectTrigger id="gender" className="w-full">
                    <SelectValue placeholder="Select Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                  Age<span className="text-red-500 ml-0.5">*</span>
                </label>
                <Select
                  value={formData.age}
                  onValueChange={(value) => handleSelectChange('age', value)}
                  disabled={!isEditing}
                >
                  <SelectTrigger id="age" className="w-full">
                    <SelectValue placeholder="Select Age Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18-24">18-24</SelectItem>
                    <SelectItem value="25-34">25-34</SelectItem>
                    <SelectItem value="34-44">34-44</SelectItem>
                    <SelectItem value="45-54">45-54</SelectItem>
                    <SelectItem value="55-64">55-64</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <textarea
                  id="bio"
                  name="bio"
                  rows={4}
                  value={formData.bio}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Tell us about yourself..."
                />
              </div>
            </div>
          </div>

          {/* Affiliate & Performance */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Affiliate & Performance</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="isAffiliate" className="block text-sm font-medium text-gray-700 mb-1">
                  Are you an affiliate creator?<span className="text-red-500 ml-0.5">*</span>
                </label>
                <Select
                  value={formData.isAffiliate}
                  onValueChange={(value) => handleSelectChange('isAffiliate', value)}
                  disabled={!isEditing}
                >
                  <SelectTrigger id="isAffiliate" className="w-full">
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="monthlyGMV" className="block text-sm font-medium text-gray-700 mb-1">
                  Average Monthly GMV (Gross Merchandise Value)
                </label>
                <div className={`flex items-center border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition duration-150 ease-in-out ${!isEditing ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="pl-3 py-2.5 flex items-center pointer-events-none">
                    <span className="text-gray-400 sm:text-sm">₱</span>
                  </div>
                  <input
                    type="number"
                    id="monthlyGMV"
                    name="monthlyGMV"
                    value={formData.monthlyGMV}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="flex-1 block w-full min-w-0 border-none bg-transparent py-2.5 pl-1 pr-3 text-sm focus:ring-0 focus:outline-none"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GMV Proof (Screenshots of your affiliate dashboard, etc.)
                </label>
                <div className="flex items-center space-x-4">
                  {isEditing ? (
                    <div className="flex-1">
                      <input
                        type="file"
                        id="gmvProof"
                        onChange={handleGmvProofChange}
                        className="hidden"
                        accept="image/*,application/pdf"
                      />
                      <label
                        htmlFor="gmvProof"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        {gmvProofFile ? 'Change Proof' : 'Upload Proof'}
                      </label>
                      {gmvProofFile && (
                        <span className="ml-3 text-sm text-gray-500">
                          {gmvProofFile.name}
                          {gmvProofFile.type === 'application/pdf' && ' (PDF)'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1">
                      {formData.gmvProofUrl ? (
                        <a
                          href={formData.gmvProofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary hover:text-primary-dark text-sm font-medium"
                        >
                          View GMV Proof
                        </a>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No GMV proof uploaded</p>
                      )}
                    </div>
                  )}
                </div>
                {gmvProofPreview && (
                  <div className="mt-4">
                    <img src={gmvProofPreview} alt="GMV Proof Preview" className="h-32 rounded-lg border border-gray-200" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content Categories */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Content Categories</h4>
            <p className="text-sm text-gray-500 mb-5">
              Select the categories that best describe your content (select up to {MAX_CATEGORIES})
            </p>

            <div className="flex flex-wrap gap-3">
              {CATEGORY_OPTIONS.map((category) => {
                const isSelected = Array.isArray(formData.categories) && formData.categories.includes(category);
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => handleCategoryChange(category)}
                    disabled={!isEditing}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                      isSelected ? 'bg-primary text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    } ${!isEditing && 'opacity-80 cursor-default'}`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Social Media Profiles */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-100">Social Media Profiles</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {SOCIAL_PLATFORMS.map(platform => (
                <SocialMediaField
                  key={platform.key}
                  platform={platform}
                  formData={formData}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                />
              ))}
            </div>
          </div>

          {/* Audience Demographics */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-800 mb-2 pb-2 border-b border-gray-100">Audience Demographics</h4>
            <p className="text-sm text-gray-500 mb-5">
              Help brands understand your audience. Estimate these from your social media analytics dashboards. All fields are optional.
            </p>

            {/* Age Range Distribution */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Age Range Distribution (%)</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {['18-24', '25-34', '35-44', '45-54', '55+'].map(range => (
                  <div key={range}>
                    <label className="text-xs text-gray-500">{range}</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.audienceDemographics?.ageRanges?.[range] || ''}
                      onChange={(e) => handleAudienceChange('ageRanges', range, e.target.value)}
                      disabled={!isEditing}
                      placeholder="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50"
                    />
                  </div>
                ))}
              </div>
              {(() => {
                const total = Object.values(formData.audienceDemographics?.ageRanges || {}).reduce((sum, v) => sum + (parseInt(v) || 0), 0);
                if (total > 0) {
                  return (
                    <div className="mt-2">
                      <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
                        {Object.entries(formData.audienceDemographics?.ageRanges || {}).map(([range, val]) => {
                          const pct = parseInt(val) || 0;
                          if (pct === 0) return null;
                          const colors = { '18-24': 'bg-blue-400', '25-34': 'bg-blue-500', '35-44': 'bg-purple-400', '45-54': 'bg-purple-500', '55+': 'bg-pink-400' };
                          return <div key={range} className={colors[range]} style={{ width: `${pct}%` }} title={`${range}: ${pct}%`} />;
                        })}
                      </div>
                      <p className={`text-xs mt-1 ${total > 100 ? 'text-red-500' : 'text-gray-500'}`}>Total: {total}%{total > 100 && ' (exceeds 100%)'}</p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Gender Split */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender Split (%)</label>
              <div className="grid grid-cols-3 gap-3">
                {['male', 'female', 'other'].map(gender => (
                  <div key={gender}>
                    <label className="text-xs text-gray-500 capitalize">{gender}</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.audienceDemographics?.genderSplit?.[gender] || ''}
                      onChange={(e) => handleAudienceChange('genderSplit', gender, e.target.value)}
                      disabled={!isEditing}
                      placeholder="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50"
                    />
                  </div>
                ))}
              </div>
              {(() => {
                const total = Object.values(formData.audienceDemographics?.genderSplit || {}).reduce((sum, v) => sum + (parseInt(v) || 0), 0);
                if (total > 0) {
                  return (
                    <div className="mt-2">
                      <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
                        {Object.entries(formData.audienceDemographics?.genderSplit || {}).map(([gender, val]) => {
                          const pct = parseInt(val) || 0;
                          if (pct === 0) return null;
                          const colors = { male: 'bg-blue-500', female: 'bg-pink-500', other: 'bg-purple-400' };
                          return <div key={gender} className={colors[gender]} style={{ width: `${pct}%` }} title={`${gender}: ${pct}%`} />;
                        })}
                      </div>
                      <p className={`text-xs mt-1 ${total > 100 ? 'text-red-500' : 'text-gray-500'}`}>Total: {total}%{total > 100 && ' (exceeds 100%)'}</p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Top Locations */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Top Locations (City/Country)</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[0, 1, 2].map(index => (
                  <input
                    key={index}
                    type="text"
                    value={formData.audienceDemographics?.topLocations?.[index] || ''}
                    onChange={(e) => handleAudienceLocationChange(index, e.target.value)}
                    disabled={!isEditing}
                    placeholder={`Location ${index + 1}`}
                    className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50"
                  />
                ))}
              </div>
            </div>

            {/* Interests / Topics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audience Interests / Topics</label>
              {isEditing && (
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={interestInput}
                    onChange={(e) => setInterestInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInterestAdd(); } }}
                    placeholder="Type an interest and press Enter (max 10)"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={handleInterestAdd}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md transition-colors"
                  >
                    Add
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {(formData.audienceDemographics?.interests || []).map(interest => (
                  <span key={interest} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                    {interest}
                    {isEditing && (
                      <button
                        type="button"
                        onClick={() => handleInterestRemove(interest)}
                        className="ml-2 text-gray-400 hover:text-red-500"
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
                {(formData.audienceDemographics?.interests || []).length === 0 && (
                  <p className="text-sm text-gray-400">No interests added yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={saving}
                className="inline-flex justify-center py-2.5 px-6 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex justify-center items-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="xs" color="white" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  'Save Profile'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Profile;
